import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Briefcase, Plus, Search, Loader2, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PrestationIdentite } from '../components/prestations/PrestationParts';
import { TYPE_PRESTATION, STATUT_PRESTATION, STATUTS_PRESTATION, emptyPrestation, fmtDate } from '../lib/prestations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => (Number(v) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export default function PrestationsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState('tous');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPrestation());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([axios.get(`${API_URL}/prestations`), axios.get(`${API_URL}/societes`)]);
      setItems(p.data || []);
      setSocietes(s.data || []);
    } catch { toast.error('Erreur de chargement'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get('nouveau') === '1') {
      setForm(emptyPrestation({ type_prestation: params.get('type') || 'consulting', intitule: params.get('intitule') || '', client: params.get('client') || '', client_id: params.get('client_id') || null, projet_commercial_id: params.get('projet') || null, description: params.get('besoin') || '' }));
      setOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.intitule.trim()) return toast.error("L'intitulé est obligatoire");
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/prestations`, form);
      toast.success(`${res.data.code} créée`);
      setOpen(false);
      navigate(`/prestations/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la création'); } finally { setSaving(false); }
  };

  const filtered = useMemo(() => items.filter(p => (statut === 'tous' || p.statut === statut) && (!search || `${p.code} ${p.intitule} ${p.client || ''}`.toLowerCase().includes(search.toLowerCase()))), [items, statut, search]);

  return (
    <DashboardLayout>
    <div className="space-y-6" data-testid="prestations-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Briefcase className="w-6 h-6 text-fuchsia-600" /> Prestations</h1>
          <p className="text-sm text-slate-500">Dossiers de consulting, de gestion et autres prestations — hors actions de formation</p>
        </div>
        <Button onClick={() => { setForm(emptyPrestation()); setOpen(true); }} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="new-prestation-btn"><Plus className="w-4 h-4 mr-2" /> Nouvelle prestation</Button>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Rechercher (code, intitulé, client)…" value={search} onChange={e => setSearch(e.target.value)} data-testid="prestations-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[{ value: 'tous', label: 'Tous' }, ...STATUTS_PRESTATION].map(s => (
            <button key={s.value} type="button" onClick={() => setStatut(s.value)} className={`text-xs px-3 py-1.5 rounded-full border ${statut === s.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-testid={`prestations-filtre-${s.value}`}>{s.label}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        : filtered.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-500">Aucune prestation{search || statut !== 'tous' ? ' ne correspond aux filtres' : ' — créez la première'}.</CardContent></Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="grid grid-cols-[120px_90px_1fr_1fr_110px_110px_100px] gap-3 px-4 py-2 bg-slate-50 text-[10px] uppercase text-slate-400 font-medium">
              <span>Code</span><span>Type</span><span>Intitulé</span><span>Client</span><span>Créée le</span><span>Statut</span><span className="text-right">CA</span>
            </div>
            <div className="divide-y">
              {filtered.map(p => (
                <button key={p.id} type="button" onClick={() => navigate(`/prestations/${p.id}`)} className="w-full grid grid-cols-[120px_90px_1fr_1fr_110px_110px_100px] gap-3 px-4 py-3 text-left text-sm hover:bg-slate-50 items-center" data-testid={`prestation-row-${p.id}`}>
                  <span className="font-mono text-xs font-semibold text-fuchsia-700">{p.code}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium w-fit ${TYPE_PRESTATION[p.type_prestation]?.cls}`}>{TYPE_PRESTATION[p.type_prestation]?.label}</span>
                  <span className="font-medium text-slate-900 truncate">{p.intitule}</span>
                  <span className="text-slate-600 truncate">{p.client || '—'}</span>
                  <span className="text-xs text-slate-500">{fmtDate((p.created_at || '').slice(0, 10))}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium w-fit ${STATUT_PRESTATION[p.statut]?.cls}`}>{STATUT_PRESTATION[p.statut]?.label}</span>
                  <span className="text-right text-xs font-semibold text-slate-700 flex items-center justify-end gap-1">{p.nb_factures > 0 && <Receipt className="w-3 h-3 text-emerald-600" />}{eur(p.ca)}</span>
                </button>
              ))}
            </div>
          </Card>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="prestation-create-dialog">
          <DialogHeader><DialogTitle>Nouvelle prestation</DialogTitle><DialogDescription>Un code PRS-AAAA-xxxx sera attribué automatiquement. Le financement (bons de commande) et la facturation libre se gèrent ensuite dans le dossier.</DialogDescription></DialogHeader>
          <PrestationIdentite form={form} set={set} societes={societes} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="button" className="bg-fuchsia-600 hover:bg-fuchsia-700" disabled={saving} onClick={save} data-testid="prestation-create-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer la prestation'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
